import { motion } from "framer-motion";
import { FileCode, Play, AlertTriangle, CheckCircle2 } from "lucide-react";

export function StatsCards({ testCasesCount, executions, loading }) {
  const total = executions.length;
  const passed = executions.filter((e) => e.status === "completed" && e.result === "passed").length;
  const failed = executions.filter((e) => e.status === "completed" && e.result === "failed").length;
  const passRate = total > 0
    ? Math.round((passed / total) * 100)
    : 0;

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="glass-card p-5 rounded-xl animate-pulse">
            <div className="space-y-3">
              <div className="h-3 w-20 bg-slate-200 rounded" />
              <div className="h-8 w-16 bg-slate-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {/* Failures — prominent, red border */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-5 rounded-xl border-2 border-red-300 bg-red-50"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-red-500 uppercase tracking-wide mb-1">Failures</p>
            <p
              className="text-3xl font-bold text-red-600"
              style={{ fontFamily: "Unbounded, sans-serif" }}
              data-testid="stat-failures"
            >
              {failed}
            </p>
            <p className="text-xs text-red-400 mt-1">from {total} runs</p>
          </div>
          <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center mt-0.5">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
        </div>
      </motion.div>

      {/* Pass Rate */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="glass-card p-5 rounded-xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Pass Rate</p>
            <p
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Unbounded, sans-serif" }}
              data-testid="stat-success-rate"
            >
              {passRate}%
            </p>
            <p className="text-xs text-slate-400 mt-1">{passed} passed</p>
          </div>
          <div className="w-9 h-9 bg-[#00b37a]/10 rounded-lg flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-5 h-5 text-[#00b37a]" />
          </div>
        </div>
      </motion.div>

      {/* Total Tests */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card p-5 rounded-xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Total Tests</p>
            <p
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Unbounded, sans-serif" }}
              data-testid="stat-total-tests"
            >
              {testCasesCount}
            </p>
            <p className="text-xs text-slate-400 mt-1">test cases</p>
          </div>
          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center mt-0.5">
            <FileCode className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      </motion.div>

      {/* Executions */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="glass-card p-5 rounded-xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Executions</p>
            <p
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Unbounded, sans-serif" }}
              data-testid="stat-executions"
            >
              {total}
            </p>
            <p className="text-xs text-slate-400 mt-1">total runs</p>
          </div>
          <div className="w-9 h-9 bg-purple-50 rounded-lg flex items-center justify-center mt-0.5">
            <Play className="w-5 h-5 text-purple-500" />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
